import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LayoutDashboard, CalendarCheck, FileText, Sparkles, Users, Settings as SettingsIcon, Download, LogOut, DollarSign, CalendarClock, Clock, Shield, TicketPercent, Megaphone, Inbox, Star } from "lucide-react";
import { api, BACKEND_URL } from "./adminShared";
import { useAuth } from "@/context/AuthContext";
import AdminBookings from "./AdminBookings";
import AdminEstimates from "./AdminEstimates";
import AdminServices from "./AdminServices";
import AdminSettings from "./AdminSettings";
import AdminCustomers from "./AdminCustomers";
import AdminCalendar from "./AdminCalendar";
import AdminStaff from "./AdminStaff";
import AdminDiscounts from "./AdminDiscounts";
import AdminMarketing from "./AdminMarketing";
import AdminLeads from "./AdminLeads";
import AdminReviews from "./AdminReviews";

const TABS = [
  ["overview", "Overview", LayoutDashboard],
  ["bookings", "Bookings", CalendarCheck],
  ["calendar", "Calendar", CalendarClock],
  ["estimates", "Estimates", FileText],
  ["leads", "Leads", Inbox],
  ["reviews", "Reviews", Star],
  ["services", "Services & Pricing", Sparkles],
  ["customers", "Customers", Users],
  ["discounts", "Discounts", TicketPercent],
  ["marketing", "Marketing", Megaphone],
  ["staff", "Staff Access", Shield],
  ["settings", "Settings", SettingsIcon],
];
const STAFF_VISIBLE = new Set(["bookings", "calendar", "estimates", "leads", "reviews", "customers"]);

function Overview({ stats }) {
  const cards = [
    { label: "Total bookings", value: stats?.total_bookings ?? "—", icon: CalendarCheck },
    { label: "Revenue (booked)", value: stats ? `$${stats.revenue.toFixed(2)}` : "—", icon: DollarSign },
    { label: "Today's appointments", value: stats?.today_appointments ?? "—", icon: CalendarClock },
    { label: "Pending estimates", value: stats?.pending_estimates ?? "—", icon: FileText },
    { label: "Awaiting approval", value: stats?.pending_approval ?? "—", icon: Clock },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-overview">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-2xl border border-[#E6DEC3] p-6 shadow-[0_4px_20px_rgba(44,38,33,0.04)]">
          <c.icon className="text-[#B87333]" size={22} />
          <p className="mt-3 text-sm text-[#635B52]">{c.label}</p>
          <p className="font-serif text-3xl text-[#2C2621]">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isOwner = user?.role === "owner" || user?.role === "admin";
  const marketingDraft = !!(user?.permissions && user.permissions.marketing_draft);
  const tabs = isOwner ? TABS : TABS.filter(([id]) => STAFF_VISIBLE.has(id) || (id === "marketing" && marketingDraft));
  const [tab, setTab] = useState(isOwner ? "overview" : "bookings");
  const [stats, setStats] = useState(null);

  useEffect(() => { if (isOwner) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, [tab, isOwner]);

  const doLogout = async () => { await logout(); navigate("/"); };
  const exportCsv = async (kind) => {
    try {
      const res = await api.get(`/admin/export/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${kind}.csv`; a.click();
      URL.revokeObjectURL(url); toast.success(`Exported ${kind}.csv`);
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F5] flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 bg-[#1E1A17] lg:min-h-screen">
        <div className="p-5 flex items-center justify-between lg:block">
          <Link to="/"><img src="/masaya-logo.png" alt="Masaya" className="h-12 w-auto brightness-0 invert" /></Link>
          <button onClick={doLogout} className="lg:hidden text-[#E6DEC3]"><LogOut size={20} /></button>
        </div>
        <nav className="px-3 pb-4 flex lg:flex-col gap-1 overflow-x-auto">
          {tabs.map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)} className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors ${tab === k ? "bg-[#B87333] text-white" : "text-[#B8AE9C] hover:bg-white/5"}`} data-testid={`admin-tab-${k}`}>
              <Icon size={17} /> {label}
            </button>
          ))}
          <button onClick={doLogout} className="hidden lg:inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-[#B8AE9C] hover:bg-white/5 mt-4" data-testid="admin-logout"><LogOut size={17} /> Logout</button>
        </nav>
      </aside>

      {/* Main */}
      <main className="flex-1 p-5 sm:p-8 max-w-full overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-serif text-3xl text-[#2C2621]">{(tabs.find((t) => t[0] === tab) || tabs[0])[1]}</h1>
            <p className="text-sm text-[#635B52]">{isOwner ? "Masaya Owner Portal" : "Masaya Staff Portal"}</p>
          </div>
          {isOwner && (tab === "bookings" || tab === "customers" || tab === "overview") && (
            <div className="flex gap-2">
              <button onClick={() => exportCsv("bookings")} className="inline-flex items-center gap-1 border border-[#E6DEC3] bg-white rounded-full px-4 py-2 text-sm text-[#2C2621]" data-testid="export-bookings"><Download size={14} /> Bookings CSV</button>
              <button onClick={() => exportCsv("customers")} className="inline-flex items-center gap-1 border border-[#E6DEC3] bg-white rounded-full px-4 py-2 text-sm text-[#2C2621]" data-testid="export-customers"><Download size={14} /> Customers CSV</button>
            </div>
          )}
        </div>

        {tab === "overview" && <Overview stats={stats} />}
        {tab === "bookings" && <AdminBookings />}
        {tab === "calendar" && <AdminCalendar />}
        {tab === "estimates" && <AdminEstimates />}
        {tab === "leads" && <AdminLeads />}
        {tab === "reviews" && <AdminReviews />}
        {tab === "services" && <AdminServices />}
        {tab === "customers" && <AdminCustomers />}
        {tab === "discounts" && isOwner && <AdminDiscounts />}
        {tab === "marketing" && (isOwner || marketingDraft) && <AdminMarketing />}
        {tab === "staff" && isOwner && <AdminStaff />}
        {tab === "settings" && <AdminSettings />}
      </main>
    </div>
  );
}
